// src/components/UpgradeGate.tsx
//
// Full-page locked-feature state, shown when the merchant's tier does not include a
// feature. Displays WHAT the feature is + a CTA to the billing page to upgrade — so
// merchants can SEE what they're missing (the strategy's upsell lever).
//
// NEVER shows a price: per non-negotiable rule #4, price appears only on a demo call,
// never in the product/marketing copy. The CTA routes to /app/billing where the plan
// is chosen.

import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles } from "lucide-react";
import {
  FEATURE_LABEL,
  TIER_LABEL,
  minTierForFeature,
  type Feature,
} from "@/lib/tier";

interface UpgradeGateProps {
  feature: Feature;
  /** Optional one-line value description; defaults to a generic line. */
  description?: string;
  /** Optional bullets describing what the feature does (no prices). */
  highlights?: string[];
}

export default function UpgradeGate({
  feature,
  description,
  highlights,
}: UpgradeGateProps) {
  const navigate = useNavigate();
  const requiredTier = minTierForFeature(feature);
  const tierName = requiredTier ? TIER_LABEL[requiredTier] : "a higher plan";

  return (
    <div className="flex items-center justify-center py-16">
      <Card className="max-w-lg w-full">
        <CardContent className="pt-8 pb-8 text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-8 w-8 text-primary" />
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold">{FEATURE_LABEL[feature]}</h2>
            <p className="text-muted-foreground">
              {description ??
                `${FEATURE_LABEL[feature]} is part of the ${tierName} plan.`}
            </p>
            <p className="text-sm font-semibold text-primary">
              Available on {tierName}
            </p>
          </div>

          {highlights && highlights.length > 0 && (
            <ul className="text-left space-y-3 px-4">
              {highlights.map((h) => (
                <li key={h} className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{h}</span>
                </li>
              ))}
            </ul>
          )}

          <Button
            size="lg"
            className="w-full"
            onClick={() => navigate("/app/billing")}
          >
            Upgrade to {tierName}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
